import { AbstractModel } from '@patsnap/tahiti-core';
import { NoveltyCheckStepInfoModel, type NoveltyCheckStepInfoState } from '../novelty-check-step-info-model';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { NoveltyCheckTaskStep } from '../../enums';
import { NoveltyCheckStepInfoStatus, type NoveltyCheckStepInfo } from '../../types';
import _ from 'lodash';

/**
 * 创建初始查新步骤信息状态
 */
function createInitialNoveltyCheckStepInfoState(): NoveltyCheckStepInfoState {
    const stepsInfo = {} as Record<NoveltyCheckTaskStep, NoveltyCheckStepInfo>;

    return {
        stepsInfo,
    };
}

@injectable()
export class NoveltyCheckStepInfoModelImpl extends AbstractModel<NoveltyCheckStepInfoState> implements NoveltyCheckStepInfoModel {
    protected _modelData: NoveltyCheckStepInfoState = createInitialNoveltyCheckStepInfoState();

    dispose(): void {
        this.update(() => createInitialNoveltyCheckStepInfoState());
    }

    setStepStatus(step: NoveltyCheckTaskStep, status: NoveltyCheckStepInfoStatus): void {
        this.update(state => {
            const cloned = _.cloneDeep(state);
            let stepInfo = cloned.stepsInfo[step];
            if (!stepInfo) {
                cloned.stepsInfo[step] = {
                    status: NoveltyCheckStepInfoStatus.PENDING,
                    waterflow: {},
                };
            }
            stepInfo = cloned.stepsInfo[step];
            const previousStatus = stepInfo.status;
            stepInfo.status = status;
            const now = Date.now();
            stepInfo.waterflow[status] = now;

            // 如果是错误状态，需要特殊处理
            if (status === NoveltyCheckStepInfoStatus.ERROR) {
                if (previousStatus === NoveltyCheckStepInfoStatus.ENTERING) {
                    // 如果正在进入步骤，则idle为error出现时间
                    stepInfo.waterflow[NoveltyCheckStepInfoStatus.IDLE] = now;
                } else if (previousStatus === NoveltyCheckStepInfoStatus.LEAVING) {
                    // 如果正在离开步骤，则left为error出现时间
                    stepInfo.waterflow[NoveltyCheckStepInfoStatus.LEFT] = now;
                }
            }
            return cloned;
        });
    }

    getStepSpendTime(step: NoveltyCheckTaskStep): number {
        const stepInfo = this.getStepInfo(step);
        if (!stepInfo) {
            return 0;
        }

        const waterflow = stepInfo.waterflow;
        let totalTime = 0;

        // 计算进入步骤花费的时间
        if (waterflow[NoveltyCheckStepInfoStatus.ENTERING] && waterflow[NoveltyCheckStepInfoStatus.IDLE]) {
            const enterSpent = waterflow[NoveltyCheckStepInfoStatus.IDLE] - waterflow[NoveltyCheckStepInfoStatus.ENTERING];
            totalTime += enterSpent;
        }

        // 计算离开步骤花费的时间
        if (waterflow[NoveltyCheckStepInfoStatus.LEAVING] && waterflow[NoveltyCheckStepInfoStatus.LEFT]) {
            const leavingSpent = waterflow[NoveltyCheckStepInfoStatus.LEFT] - waterflow[NoveltyCheckStepInfoStatus.LEAVING];
            totalTime += leavingSpent;
        }

        return totalTime;
    }

    getStepInfo(step: NoveltyCheckTaskStep): NoveltyCheckStepInfo {
        return this.modelData.stepsInfo[step];
    }

    isStepCompleted(step: NoveltyCheckTaskStep): boolean {
        const stepInfo = this.getStepInfo(step);
        return stepInfo?.status === NoveltyCheckStepInfoStatus.LEFT;
    }

    isStepLocked(step: NoveltyCheckTaskStep): boolean {
        const stepInfo = this.getStepInfo(step);
        return [NoveltyCheckStepInfoStatus.ENTERING, NoveltyCheckStepInfoStatus.LEAVING].includes(stepInfo?.status);
    }

    isStepWaitingForConfirmation(step: NoveltyCheckTaskStep): boolean {
        const stepInfo = this.getStepInfo(step);
        return stepInfo?.status === NoveltyCheckStepInfoStatus.IDLE;
    }

    isStepEntering(step: NoveltyCheckTaskStep): boolean {
        const stepInfo = this.getStepInfo(step);
        return stepInfo?.status === NoveltyCheckStepInfoStatus.ENTERING;
    }

    resetStep(step: NoveltyCheckTaskStep): void {
        this.update(state => {
            const cloned = _.cloneDeep(state);
            delete cloned.stepsInfo[step];
            return cloned;
        });
    }
}
